import type { MultipleChoicePublicPrompt } from '../../../lib/api/types.js';
import type { ExerciseRendererProps } from '../registry.ts';
import { Text } from '../../../ui/primitives/Text.tsx';
import { TextChoiceExercise } from '../text-choice/TextChoiceExercise.tsx';
import styles from './MultipleChoiceRenderer.module.css';

/** A guard, not a cast — a malformed prompt is a server contract violation.
    Exported for `index.ts`'s answer describer, which reads a prompt recorded
    against an attempt rather than one handed to a renderer. */
export function isMultipleChoicePrompt(value: unknown): value is MultipleChoicePublicPrompt {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { question?: unknown; options?: unknown };
  return (
    typeof candidate.question === 'string' &&
    Array.isArray(candidate.options) &&
    candidate.options.every((option) => typeof option === 'string')
  );
}

/**
 * `multiple_choice`: a text question, presented once — every option is
 * answerable the moment the Exercise appears, unlike `note_identification`,
 * which gates the grid on having heard its clip first.
 */
export function MultipleChoiceRenderer({
  promptId,
  publicPrompt,
  onSubmit,
  phase,
  feedback,
}: ExerciseRendererProps) {
  if (!isMultipleChoicePrompt(publicPrompt)) {
    throw new Error('MultipleChoiceRenderer received a publicPrompt that is not { question, options }');
  }

  return (
    <TextChoiceExercise
      promptId={promptId}
      options={publicPrompt.options}
      answerable
      phase={phase}
      feedback={feedback}
      onSubmit={onSubmit}
      prompt={
        <div className={styles.question}>
          <Text as="h1" variant="heading">
            {publicPrompt.question}
          </Text>
        </div>
      }
    />
  );
}
